import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Save, Eye, Palette, Type, Copy } from 'lucide-react'
import { themeAPI } from '../services/api'
import { useTheme } from '../contexts/ThemeContext'
import toast from 'react-hot-toast'

interface ThemeCustomizerProps {
  baseTheme?: any
  onClose: () => void
  onSave?: () => void
}

const FONT_OPTIONS = [
  { value: 'Inter, system-ui, sans-serif', label: 'Inter (Default)' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet MS' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: '"Comic Sans MS", cursive', label: 'Comic Sans MS' },
  { value: 'Impact, fantasy', label: 'Impact' },
  { value: '"Lucida Console", monospace', label: 'Lucida Console' },
  { value: 'Garamond, serif', label: 'Garamond' },
  { value: '"Palatino Linotype", serif', label: 'Palatino' },
  { value: '"Arial Black", sans-serif', label: 'Arial Black' },
]

const BORDER_RADIUS_OPTIONS = [
  { value: '0px', label: 'None (Square)' },
  { value: '4px', label: 'Small' },
  { value: '8px', label: 'Medium (Default)' },
  { value: '12px', label: 'Large' },
  { value: '16px', label: 'Extra Large' },
  { value: '24px', label: 'Rounded' },
]

const FONT_SIZE_OPTIONS = [
  { value: '12px', label: 'Small' },
  { value: '14px', label: 'Medium (Default)' },
  { value: '16px', label: 'Large' },
  { value: '18px', label: 'Extra Large' },
]

export default function ThemeCustomizer({ baseTheme, onClose, onSave }: ThemeCustomizerProps) {
  const { currentTheme, setTheme, refreshThemes } = useTheme()
  const [saving, setSaving] = useState(false)
  const [previewMode, setPreviewMode] = useState(true)
  
  const [themeName, setThemeName] = useState('')
  const [colors, setColors] = useState({
    primary_color: '#0183c6',
    secondary_color: '#10B981',
    accent_color: '#F59E0B',
    background_color: '#F9FAFB',
    surface_color: '#FFFFFF',
    text_primary: '#111827',
    text_secondary: '#6B7280',
    border_color: '#E5E7EB',
    widget_background: '#FFFFFF',
    widget_border: '#E5E7EB',
  })
  
  const [typography, setTypography] = useState({
    font_family: 'Inter, system-ui, sans-serif',
    font_size_base: '14px',
    border_radius: '8px',
  })
  
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    if (baseTheme) {
      setThemeName(baseTheme.name + ' (Custom)')
      setColors({
        primary_color: baseTheme.primary_color || '#0183c6',
        secondary_color: baseTheme.secondary_color || '#10B981',
        accent_color: baseTheme.accent_color || '#F59E0B',
        background_color: baseTheme.background_color || '#F9FAFB',
        surface_color: baseTheme.surface_color || '#FFFFFF',
        text_primary: baseTheme.text_primary || '#111827',
        text_secondary: baseTheme.text_secondary || '#6B7280',
        border_color: baseTheme.border_color || '#E5E7EB',
        widget_background: baseTheme.widget_background || '#FFFFFF',
        widget_border: baseTheme.widget_border || '#E5E7EB',
      })
      setTypography({
        font_family: baseTheme.font_family || 'Inter, system-ui, sans-serif',
        font_size_base: baseTheme.font_size_base || '14px',
        border_radius: baseTheme.border_radius || '8px',
      })
      setIsDark(baseTheme.is_dark || false)
    } else if (currentTheme) {
      setThemeName('My Custom Theme')
      setColors({
        primary_color: currentTheme.primary_color || '#0183c6',
        secondary_color: currentTheme.secondary_color || '#10B981',
        accent_color: currentTheme.accent_color || '#F59E0B',
        background_color: currentTheme.background_color || '#F9FAFB',
        surface_color: currentTheme.surface_color || '#FFFFFF',
        text_primary: currentTheme.text_primary || '#111827',
        text_secondary: currentTheme.text_secondary || '#6B7280',
        border_color: currentTheme.border_color || '#E5E7EB',
        widget_background: currentTheme.widget_background || '#FFFFFF',
        widget_border: currentTheme.widget_border || '#E5E7EB',
      })
      setTypography({
        font_family: currentTheme.font_family || 'Inter, system-ui, sans-serif',
        font_size_base: currentTheme.font_size_base || '14px',
        border_radius: currentTheme.border_radius || '8px',
      })
      setIsDark(currentTheme.is_dark || false)
    }
  }, [baseTheme, currentTheme])

  // Apply preview colors to CSS variables
  useEffect(() => {
    if (previewMode) {
      const root = document.documentElement
      root.style.setProperty('--color-primary', colors.primary_color)
      root.style.setProperty('--color-secondary', colors.secondary_color)
      root.style.setProperty('--color-accent', colors.accent_color)
      root.style.setProperty('--color-background', colors.background_color)
      root.style.setProperty('--color-surface', colors.surface_color)
      root.style.setProperty('--color-text-primary', colors.text_primary)
      root.style.setProperty('--color-text-secondary', colors.text_secondary)
      root.style.setProperty('--color-border', colors.border_color)
      root.style.setProperty('--color-widget-background', colors.widget_background)
      root.style.setProperty('--color-widget-border', colors.widget_border)
      root.style.setProperty('--font-family', typography.font_family)
      root.style.setProperty('--font-size-base', typography.font_size_base)
      root.style.setProperty('--border-radius', typography.border_radius)
    }
  }, [colors, typography, previewMode])

  // Cleanup on unmount - restore current theme
  useEffect(() => {
    return () => {
      if (currentTheme) {
        const root = document.documentElement
        root.style.setProperty('--color-primary', currentTheme.primary_color)
        root.style.setProperty('--color-secondary', currentTheme.secondary_color)
        root.style.setProperty('--color-accent', currentTheme.accent_color)
        root.style.setProperty('--color-background', currentTheme.background_color)
        root.style.setProperty('--color-surface', currentTheme.surface_color)
        root.style.setProperty('--color-text-primary', currentTheme.text_primary)
        root.style.setProperty('--color-text-secondary', currentTheme.text_secondary)
        root.style.setProperty('--color-border', currentTheme.border_color)
        root.style.setProperty('--color-widget-background', currentTheme.widget_background)
        root.style.setProperty('--color-widget-border', currentTheme.widget_border)
        root.style.setProperty('--font-family', currentTheme.font_family)
        root.style.setProperty('--font-size-base', currentTheme.font_size_base)
        root.style.setProperty('--border-radius', currentTheme.border_radius)
      }
    }
  }, [currentTheme])

  const handleColorChange = (key: string, value: string) => {
    setColors(prev => ({ ...prev, [key]: value }))
  }

  const handleTypographyChange = (key: string, value: string) => {
    setTypography(prev => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    if (!themeName.trim()) {
      toast.error('Please enter a theme name')
      return
    }

    setSaving(true)
    try {
      const { data } = await themeAPI.create({
        name: themeName,
        is_dark: isDark,
        ...colors,
        ...typography,
        widget_shadow: isDark ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
      })

      // Set the new theme as active
      await themeAPI.setUserTheme(data.id)
      await refreshThemes() // Refresh theme list
      setTheme(data)
      
      toast.success('Custom theme saved!')
      onSave?.()
      onClose()
    } catch (error: any) {
      console.error('Failed to save theme:', error)
      toast.error(error.response?.data?.error || 'Failed to save theme')
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{ 
          backgroundColor: colors.surface_color,
          color: colors.text_primary,
          fontFamily: typography.font_family,
          fontSize: typography.font_size_base,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: colors.border_color }}
        >
          <div className="flex items-center gap-3">
            <Palette size={24} style={{ color: colors.primary_color }} />
            <h2 className="text-2xl font-bold">Customize Theme</h2>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border transition hover:opacity-80"
              style={{ 
                borderColor: colors.border_color,
                color: previewMode ? colors.primary_color : colors.text_secondary
              }}
              title={previewMode ? 'Preview enabled' : 'Preview disabled'}
            >
              <Eye size={18} />
              <span className="text-sm">Preview</span>
            </button>
            
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition hover:opacity-80"
              style={{ color: colors.text_secondary }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Colors */}
            <div className="space-y-6">
              {/* Theme Info */}
              <div>
                <label className="block text-sm font-medium mb-2">Theme Name</label>
                <input
                  type="text"
                  value={themeName}
                  onChange={(e) => setThemeName(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border"
                  style={{
                    borderColor: colors.border_color,
                    backgroundColor: colors.background_color,
                    color: colors.text_primary,
                  }}
                  placeholder="My Custom Theme"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isDark}
                    onChange={(e) => setIsDark(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  Dark Mode
                </label>
              </div>

              {/* Primary Colors */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Palette size={20} style={{ color: colors.primary_color }} />
                  Primary Colors
                </h3>
                <div className="space-y-3">
                  {[
                    { key: 'primary_color', label: 'Primary (Buttons, Links)' },
                    { key: 'secondary_color', label: 'Secondary' },
                    { key: 'accent_color', label: 'Accent' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-3">
                      <input
                        type="color"
                        value={colors[key as keyof typeof colors]}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className="w-12 h-12 rounded-lg border cursor-pointer"
                        style={{ borderColor: colors.border_color }}
                      />
                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-1">{label}</label>
                        <input
                          type="text"
                          value={colors[key as keyof typeof colors]}
                          onChange={(e) => handleColorChange(key, e.target.value)}
                          className="w-full px-3 py-1.5 rounded border text-sm"
                          style={{
                            borderColor: colors.border_color,
                            backgroundColor: colors.background_color,
                            color: colors.text_primary,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Background Colors */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Background & Surface</h3>
                <div className="space-y-3">
                  {[
                    { key: 'background_color', label: 'Background (Page)' },
                    { key: 'surface_color', label: 'Surface (Cards, Modals)' },
                    { key: 'widget_background', label: 'Widget Background' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-3">
                      <input
                        type="color"
                        value={colors[key as keyof typeof colors]}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className="w-12 h-12 rounded-lg border cursor-pointer"
                        style={{ borderColor: colors.border_color }}
                      />
                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-1">{label}</label>
                        <input
                          type="text"
                          value={colors[key as keyof typeof colors]}
                          onChange={(e) => handleColorChange(key, e.target.value)}
                          className="w-full px-3 py-1.5 rounded border text-sm"
                          style={{
                            borderColor: colors.border_color,
                            backgroundColor: colors.background_color,
                            color: colors.text_primary,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Text & Border Colors */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Text & Borders</h3>
                <div className="space-y-3">
                  {[
                    { key: 'text_primary', label: 'Primary Text' },
                    { key: 'text_secondary', label: 'Secondary Text' },
                    { key: 'border_color', label: 'Border Color' },
                    { key: 'widget_border', label: 'Widget Border' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-3">
                      <input
                        type="color"
                        value={colors[key as keyof typeof colors]}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className="w-12 h-12 rounded-lg border cursor-pointer"
                        style={{ borderColor: colors.border_color }}
                      />
                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-1">{label}</label>
                        <input
                          type="text"
                          value={colors[key as keyof typeof colors]}
                          onChange={(e) => handleColorChange(key, e.target.value)}
                          className="w-full px-3 py-1.5 rounded border text-sm"
                          style={{
                            borderColor: colors.border_color,
                            backgroundColor: colors.background_color,
                            color: colors.text_primary,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Typography & Preview */}
            <div className="space-y-6">
              {/* Typography */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Type size={20} style={{ color: colors.primary_color }} />
                  Typography
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Font Family</label>
                    <select
                      value={typography.font_family}
                      onChange={(e) => handleTypographyChange('font_family', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border"
                      style={{
                        borderColor: colors.border_color,
                        backgroundColor: colors.background_color,
                        color: colors.text_primary,
                      }}
                    >
                      {FONT_OPTIONS.map(font => (
                        <option key={font.value} value={font.value}>{font.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Font Size</label>
                    <select
                      value={typography.font_size_base}
                      onChange={(e) => handleTypographyChange('font_size_base', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border"
                      style={{
                        borderColor: colors.border_color,
                        backgroundColor: colors.background_color,
                        color: colors.text_primary,
                      }}
                    >
                      {FONT_SIZE_OPTIONS.map(size => (
                        <option key={size.value} value={size.value}>{size.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Border Radius</label>
                    <select
                      value={typography.border_radius}
                      onChange={(e) => handleTypographyChange('border_radius', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border"
                      style={{
                        borderColor: colors.border_color,
                        backgroundColor: colors.background_color,
                        color: colors.text_primary,
                      }}
                    >
                      {BORDER_RADIUS_OPTIONS.map(radius => (
                        <option key={radius.value} value={radius.value}>{radius.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Preview</h3>
                <div 
                  className="p-6 rounded-lg border space-y-4"
                  style={{
                    backgroundColor: colors.background_color,
                    borderColor: colors.border_color,
                  }}
                >
                  <div>
                    <h4 className="font-bold mb-2" style={{ color: colors.text_primary }}>
                      Sample Heading
                    </h4>
                    <p style={{ color: colors.text_secondary }}>
                      This is a preview of your theme. Here's some secondary text to see the contrast.
                    </p>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <button
                      className="px-4 py-2 rounded text-white font-medium"
                      style={{ 
                        backgroundColor: colors.primary_color,
                        borderRadius: typography.border_radius,
                      }}
                    >
                      Primary Button
                    </button>
                    <button
                      className="px-4 py-2 rounded text-white font-medium"
                      style={{ 
                        backgroundColor: colors.secondary_color,
                        borderRadius: typography.border_radius,
                      }}
                    >
                      Secondary Button
                    </button>
                    <button
                      className="px-4 py-2 rounded border font-medium"
                      style={{ 
                        borderColor: colors.border_color,
                        color: colors.text_primary,
                        borderRadius: typography.border_radius,
                      }}
                    >
                      Outline Button
                    </button>
                  </div>

                  <div 
                    className="p-4 rounded border"
                    style={{
                      backgroundColor: colors.widget_background,
                      borderColor: colors.widget_border,
                      borderRadius: typography.border_radius,
                    }}
                  >
                    <h5 className="font-semibold mb-2" style={{ color: colors.text_primary }}>
                      Widget Preview
                    </h5>
                    <p className="text-sm" style={{ color: colors.text_secondary }}>
                      This is how widgets will look with your custom theme.
                    </p>
                  </div>

                  <div 
                    className="p-4 rounded"
                    style={{
                      backgroundColor: colors.surface_color,
                      borderRadius: typography.border_radius,
                    }}
                  >
                    <h5 className="font-semibold mb-2" style={{ color: colors.text_primary }}>
                      Card/Modal Preview
                    </h5>
                    <p className="text-sm" style={{ color: colors.text_secondary }}>
                      This is how cards and modals will appear.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div 
          className="flex items-center justify-between p-6 border-t"
          style={{ borderColor: colors.border_color }}
        >
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg border font-medium transition hover:opacity-80"
            style={{
              borderColor: colors.border_color,
              color: colors.text_primary,
              borderRadius: typography.border_radius,
            }}
          >
            Cancel
          </button>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 rounded-lg text-white font-medium transition hover:opacity-90 disabled:opacity-50"
            style={{
              backgroundColor: colors.primary_color,
              borderRadius: typography.border_radius,
            }}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={18} />
                <span>Save Theme</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

